import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateVendorMutation } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import { formatPhoneNumber, PHONE_NUMBER_PLACEHOLDER } from "pages/formTools";
import logger from "utils/logger";
import { DashboardProfileData } from "../../../..";
import { ProfileEditButton } from "../../../../components/ProfileEditButton";
import GoogleListingStatus from "../GoogleListingStatus";
import GoogleReviewsLinkHelp from "../GoogleReviewsLinkHelp";

interface BrandIdentityFieldsProps {
  dashboardProfileData: DashboardProfileData;
  onChange: (name: string, value: string | boolean) => void;
}

/** What customers see of the business; every field is optional. */
const BrandIdentityFields: React.FC<BrandIdentityFieldsProps> = ({
  dashboardProfileData,
  onChange,
}) => {
  const { vendor, account } = dashboardProfileData;
  const showSnack = useSnack();
  const { t } = useTranslation("profile");
  const [, updateVendor] = useUpdateVendorMutation();
  const [listingRefreshKey, setListingRefreshKey] = useState(0);

  // Resolves false on a rejected save so ProfileEditButton restores the old
  // value, e.g. a Google link that couldn't be matched to a listing.
  const handleFieldSave = async (
    updates: Record<string, string | boolean | number>,
  ): Promise<boolean> => {
    logger.log("Saving brand identity updates:", updates);
    const response = await updateVendor({ options: updates });
    if (response.error || response.data?.updateVendor.errors) {
      showSnack(
        response.data?.updateVendor.errors?.[0].message ??
          response.error?.message ??
          t("error"),
        "error",
      );
      return false;
    }

    const warning = response.data?.updateVendor.warnings?.[0]?.message;
    showSnack(warning ?? t("saved"), warning ? "warning" : "success");

    if ("brandGoogleReview" in updates) {
      setListingRefreshKey((key) => key + 1);
    }
    return true;
  };

  return (
    <>
      <ProfileEditButton
        label={t("brand.logo")}
        input={vendor?.brandLogoUrl || ""}
        name="brandLogo"
        onChange={onChange}
        onSave={handleFieldSave}
        type="logo"
      />

      <ProfileEditButton
        label={t("brand.about")}
        name="brandAbout"
        input={vendor?.brandAbout || ""}
        onChange={onChange}
        onSave={handleFieldSave}
        rows={7}
        description={t("brand.about_hint")}
      />

      <ProfileEditButton
        label={t("brand.website")}
        name="businessWebsite"
        input={vendor?.businessWebsite || ""}
        onChange={onChange}
        onSave={handleFieldSave}
        placeholder="https://www.example.com"
      />

      <ProfileEditButton
        label={t("brand.public_email")}
        name="publicEmail"
        input={vendor?.publicEmail || ""}
        onChange={onChange}
        onSave={handleFieldSave}
        description={t("brand.public_email_hint")}
      />

      <ProfileEditButton
        label={t("brand.public_phone")}
        name="publicPhone"
        input={vendor?.publicPhone || ""}
        onChange={onChange}
        onSave={handleFieldSave}
        mask={formatPhoneNumber}
        placeholder={PHONE_NUMBER_PLACEHOLDER}
        description={t("brand.public_phone_hint")}
      />

      <ProfileEditButton
        label={t("brand.google_reviews")}
        name="brandGoogleReview"
        input={vendor?.brandGoogleReview || ""}
        onChange={onChange}
        onSave={handleFieldSave}
        info={<GoogleReviewsLinkHelp />}
      />
      {account.vendorId && (
        <GoogleListingStatus
          vendorId={account.vendorId}
          refreshKey={listingRefreshKey}
        />
      )}
    </>
  );
};

export default BrandIdentityFields;
